import validator from 'validator';
import { AccountFormData } from '../types';
import { Products, NEW_PRODUCT_NAMES } from 'services/api/consts';
import { CHEQ_ACCOUNT_STATUS_OPTIONS, CLIENT_ACCOUNT_TYPE, DUMMY_SF_ACCOUNT_NAME, DUMMY_SF_ACCOUNT_CLIENT_ERROR } from '../constant';
import { RtiSettings } from 'redux/slicers/accounts/types';
import { IGetSalesforceAccountResponse } from 'models/SalesforceAccount';

export const isSpecialCharacters = (str = '') => '?,:;{[}]\\|*/+=)(^%$#!`~@&_><'.split('').some((char) => str.includes(char));

/**
 * Streamlined validation function for AccountFormScreen
 * Focuses only on validations specific to the account form to avoid duplication
 */
export const handleValidation = (
    formData: AccountFormData,
    isCreateMode: boolean,
    ossProductName = NEW_PRODUCT_NAMES[Products.OSS],
    salesforceAccounts: IGetSalesforceAccountResponse[] = []
): string[] => {
    const hasOSSProduct = formData.activeLicenses.includes(Products.OSS);
    const errors: string[] = [];

    // Required field validations
    if (!formData.salesforceAccount?.trim()) errors.push('Salesforce Account ID is required');
    if (!formData.accountName?.trim()) errors.push('Account name is required');
    if (!formData.accountEmail?.trim()) errors.push('Account email is required');
    if (!formData.accountType) errors.push('Account type is required');
    if (!formData.region?.trim()) errors.push('Region is required');
    if (!formData.activeLicenses?.length) errors.push('At least one active license is required');

    if (formData.accountName && formData.accountName.length < 3) errors.push('Account name must be at least 3 characters long');

    if (formData.accountName && isSpecialCharacters(formData.accountName)) errors.push('Account name contains invalid special characters');

    if (formData.accountEmail && !validator.isEmail(formData.accountEmail)) errors.push('Invalid email format');

    if (isCreateMode && !formData.dataLocation?.trim()) errors.push('Data location is required');

    // Conditional validations
    const selectedSalesforceAccount = salesforceAccounts.find((account) => account.ID === formData.salesforceAccount);
    if (formData.accountType === CLIENT_ACCOUNT_TYPE && selectedSalesforceAccount?.Name?.trim().toLowerCase() === DUMMY_SF_ACCOUNT_NAME.toLowerCase())
        errors.push(DUMMY_SF_ACCOUNT_CLIENT_ERROR);

    if (hasOSSProduct && !formData.defendDataType?.length) errors.push(`Data types are required when ${ossProductName} (OSS) is selected`);

    if (formData.deduceEnabled) {
        if (!formData.deduceSite?.trim()) errors.push('Deduce Site ID is required when Deduce In Use is enabled');
        if (!formData.deduceApiKey?.trim()) errors.push('Deduce Key is required when Deduce In Use is enabled');
    }

    // Status validation
    const validStatusValues = CHEQ_ACCOUNT_STATUS_OPTIONS.map((option) => option.value);
    if (typeof formData.cheqAccountStatus !== 'number' || !validStatusValues.includes(formData.cheqAccountStatus)) errors.push('Invalid CHEQ account status');

    return errors;
};

/**
 * Validates RTI settings before sending to API
 * @param rtiSettings - The RTI settings object to validate
 * @returns true if valid RTI settings, false otherwise
 */
export const validateRtiSettings = (rtiSettings: unknown): rtiSettings is RtiSettings => {
    if (!rtiSettings || typeof rtiSettings !== 'object') return false;

    const settings = rtiSettings as Record<string, unknown>;
    return (
        typeof settings.networkId === 'number' &&
        typeof settings.status === 'boolean' &&
        (settings.isApiIntegration === undefined || typeof settings.isApiIntegration === 'boolean') &&
        (settings.isClientSideIntegration === undefined || typeof settings.isClientSideIntegration === 'boolean')
    );
};
